import React, { useState, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Accordion from "./Accordion";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ReactComponent as LogoSVG } from "../../assets/images/logo.svg";
import { ReactComponent as AboutIcon } from "../../assets/images/companyIcons/about.svg";
import { ReactComponent as DetailsIcon } from "../../assets/images/companyIcons/details.svg";
import { ReactComponent as FinancialsIcon } from "../../assets/images/companyIcons/financials.svg";
import { ReactComponent as CompanyFinance } from "../../assets/images/companyIcons/CompanyFinance.svg";
import { ReactComponent as UploadIcon } from "../../assets/images/companyIcons/upload.svg";
import { clearUserInfo } from "../../store/auth/authSlice";

import {
  About,
  Industry,
  AddressContact,
  Social,
  LogoBanner,
} from "../../components/admin/Dashboard/mycompanies/CompleteCompanyProfile/About";

import {
  LicenseInformation,
  OwnershipStructure,
  AuthorizedSignatory,
  KeyPersonnel,
  RelatedEntities,
  BusinessRelation,
} from "../../components/admin/Dashboard/mycompanies/CompleteCompanyProfile/Details";

import {
  Insurance,
  BankingInformation,
  FinancialStatement,
} from "../../components/admin/Dashboard/mycompanies/CompleteCompanyProfile/Financial";

import CompanyProfileHeader from "../../components/admin/Dashboard/mycompanies/CompleteCompanyProfile/CompanyProfileHeader";

import Upload from "../../components/admin/Dashboard/mycompanies/CompleteCompanyProfile/UploadDocument/Upload";

import { ReactComponent as AboutImage } from "../../assets/images/companyIcons/upperAbout.svg";
import { ReactComponent as UploadImage } from "../../assets/images/companyIcons/UploadDocuments.svg";
import { ReactComponent as DetailsImage } from "../../assets/images/companyIcons/CompanyProfile/CompanyDetail.svg";

import {
  useGetCompanyQuery,
  useGetCompanyRequestStatusMutation,
  useGetCompanyDetailsMutation,
  useUpdateCompanyRequestStatusMutation,
} from "../../store/Company/companyApiSlice";

import { setCurrentEditingCompany } from "../../store/Company/companySlice";

import { LoadingIcon, Modal, SessionExpired } from "../../components/common";

import { FinancialSetup } from "../../components/admin/Dashboard/mycompanies/CompleteCompanyProfile/Financial/FinancialSetup";

import { TaxCategoryDetails } from "../../components/admin/Dashboard/mycompanies/CompleteCompanyProfile/Details/TaxCategoryDetails";

const CompanyEditProfile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { id } = useParams();
  const { token } = useSelector((state) => state.auth);

  const [stepIndex, setStepIndex] = useState(0);
  const isLoggedIn = localStorage.getItem("isLoggedIn") === "true"
  const [currentComponent, setCurrentComponent] = useState(null);
  const [currentAccordion, setCurrentAccordion] = useState("About");
  const [activeAccordion, setActiveAccordion] = useState("About");

  const [companyVerificationStatus, setcompanyVerificationStatus] =
    useState("pending");

  const [isSessionExpired, setSessionExpired] = useState(false);

  const [getCompanyDetails] = useGetCompanyDetailsMutation();
  const [getCompanyRequestStatus] = useGetCompanyRequestStatusMutation();
  const [updateCompanyRequestStatus] =
    useUpdateCompanyRequestStatusMutation();

  /* ========================= API ========================= */

  const { data, isLoading, isError, error, refetch } = useGetCompanyQuery({
    companyId: id,
    token,
  });

  const findProgressByKey = (key) => {
    for (let acc of accordions) {
      const index = acc.progress.findIndex(
        (p) => p.label === key
      );

      if (index !== -1) {
        return {
          accordionId: acc.id,
          accordionTitle: acc.title,
          index,
          component: acc.progress[index].component,
          label: acc.progress[index].label,
        };
      }
    }

    return null;
  };


  /* ========================= SESSION ========================= */

  useEffect(() => {
    if (isError && error?.data?.code === "token_not_valid") {
      setSessionExpired(true);
    }
  }, [isError, error]);

  const sessionExpiredPopup = () => {
    setSessionExpired(false);
    dispatch(clearUserInfo());
    navigate("/login");
  };

  /* ========================= AUTH ========================= */

  useEffect(() => {
    if (!isLoggedIn) {      
      navigate("/");
    }
  }, [isLoggedIn, navigate]);

  /* ========================= COMPANY STATUS ========================= */

  const companyStatusLatest = async () => {
    let userindividualID = localStorage.getItem("userindividualID");

    const res = await getCompanyRequestStatus({
      companyID: id,
      individualID:
        userindividualID ||
        data?.userCompany?.[0]?.individualID?.individualID,
      token,
    });

    if (res?.data?.length) {
      return res.data[res.data.length - 1];
    }

    return null;
  };

  const handleUpdateStatus = async () => {
    const resp = await companyStatusLatest();

    if (resp?.status === "Completed") {
      await updateCompanyRequestStatus({
        requestID: resp?.requestID,
        token,
      });
    }
  };

  /* ========================= DATA ========================= */

  useEffect(() => {
    if (data) {
      dispatch(setCurrentEditingCompany(data));
    }
  }, [data, dispatch]);

  const fetchCompDetails = async () => {
    const tempdata = await getCompanyDetails({
      companyId: id,
      token,
    }).unwrap();

    if (tempdata) {
      dispatch(setCurrentEditingCompany(tempdata));
    }
  };

  /* ========================= ACCORDIONS ========================= */

  const accordions = useMemo(() => {
    return [
      {
        title: "About",
        id: "About",
        icon: <AboutIcon />,

        upperComponent: (
          <CompanyProfileHeader
            Image={AboutImage}
            Heading="Complete Your Business Profile"
          />
        ),

        progress: [
          {
            label: "About Business",
            component: (
              <About
                onNext={handleNext}
                onSkip={handleSkip}
                verifyStatus={companyVerificationStatus?.status}
              />
            ),
          },
          {
            label: "Industry",
            component: (
              <Industry
                onPrev={handleBack}
                onNext={handleNext}
                onSkip={handleSkip}
              />
            ),
          },
          {
            label: "Address & Contact Information",
            component: (
              <AddressContact
                onPrev={handleBack}
                onNext={handleNext}
                onSkip={handleSkip}
                handleUpdateStatus={handleUpdateStatus}
              />
            ),
          },
          {
            label: "Social Media",
            component: (
              <Social
                onPrev={handleBack}
                onNext={handleNext}
                onSkip={handleSkip}
              />
            ),
          },
          {
            label: "Logo & Banner",
            component: (
              <LogoBanner
                onPrev={handleBack}
                onNext={handleNext}
                onSkip={handleSkip}
              />
            ),
          },
        ],
      },

      /* ===== DETAILS ===== */

      {
        title: "Details",
        id: "Details",
        icon: <DetailsIcon />,

        upperComponent: (
          <CompanyProfileHeader
            Image={DetailsImage}
            Heading="Provide Business Details"
          />
        ),

        progress: [
          {
            label: "License Information",
            component: (
              <LicenseInformation
                onPrev={handleBack}
                onNext={handleNext}
                onSkip={handleSkip}
                
              />
            ),
          },
          {
            label: "Tax Information",
            component: (
              <TaxCategoryDetails
                onPrev={handleBack}
                onNext={handleNext}
                onSkip={handleSkip}
                compRefetch={refetch}
              />
            ),
          },
          {
            label: "Ownership Structure",
            component: (
              <OwnershipStructure
                onPrev={handleBack}
                onNext={handleNext}
                onSkip={handleSkip}
                handleUpdateStatus={handleUpdateStatus}
                compRefetch={refetch}
              />
            ),
          },
          {
            label: "Authorized Signatory",
            component: (
              <AuthorizedSignatory
                onPrev={handleBack}
                onNext={handleNext}
                onSkip={handleSkip}
                handleUpdateStatus={handleUpdateStatus}
                compRefetch={refetch}
              />
            ),
          },
          {
            label: "Key Personnel",
            component: (
              <KeyPersonnel
                onPrev={handleBack}
                onNext={handleNext}
                onSkip={handleSkip}
                handleUpdateStatus={handleUpdateStatus}
                compRefetch={refetch}
              />
            ),
          },
          {
            label: "Related Entities",
            component: (
              <RelatedEntities
                onPrev={handleBack}
                onNext={handleNext}
                onSkip={handleSkip}
                handleUpdateStatus={handleUpdateStatus}
                compRefetch={refetch}
              />
            ),
          },
          {
            label: "Business Relation",
            component: (
              <BusinessRelation
                onPrev={handleBack}
                onNext={handleNext}
                onSkip={handleSkip}
                handleUpdateStatus={handleUpdateStatus}
                compRefetch={refetch}
              />
            ),
          },
        ],
      },

      /* ===== FINANCIAL ===== */

      {
        title: "Financial",
        id: "Financial",
        icon: <FinancialsIcon />,

        upperComponent: (
          <CompanyProfileHeader
            Image={CompanyFinance}
            Heading="Business Financials Details"
          />
        ),

        progress: [
          {
            label: "Banking Information",
            component: (
              <BankingInformation
                onPrev={handleBack}
                onNext={handleNext}
                onSkip={handleSkip}
              />
            ),
          },
          {
            label: "Insurance",
            component: (
              <Insurance
                onPrev={handleBack}
                onNext={handleNext}
                onSkip={handleSkip}
              />
            ),
          },
          {
            label: "Auditor Information",
            component: (
              <FinancialStatement
                onPrev={handleBack}
                onNext={handleNext}
                onSkip={handleSkip}
              />
            ),
          },
          {
            label: "Financial Documents",
            component: (
              <FinancialSetup
                onPrev={handleBack}
                onNext={handleNext}
                onSkip={handleSkip}
              />
            ),
          },
        ],
      },

      /* ===== UPLOAD ===== */

      {
        title: "Upload Document",
        id: "Upload Document",
        icon: <UploadIcon />,

        upperComponent: (
          <CompanyProfileHeader Image={UploadImage} Heading="Documents" />
        ),

        progress: [
          {
            label: "MOA",
            component: <Upload heading="Upload MOA" />,
          },
          {
            label: "Trade License",
            component: <Upload heading="Upload Trade License" />,
          },
          {
            label: "VAT Certificate",
            component: <Upload heading="VAT Certificate" />,
          },
        ],
      },
    ];
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("CurrentComponent");

    if (!saved) {
      localStorage.setItem("CurrentComponent", "About Business");
      localStorage.setItem("accordianIndex", 0);
    }
  }, []);


  /* ========================= HELPERS ========================= */

  const componentsArray = useMemo(() => {
    return accordions.map((a) => a.progress).flat();
  }, [accordions]);

  const getCurrentComponent = (label) => {
    if (!label) return;

    localStorage.setItem("CurrentComponent", label);

    const found = componentsArray.find((c) => c.label === label);

    if (found) {
      setCurrentComponent(found.component);
      setStepIndex(componentsArray.indexOf(found));
    }
  };

  /* ========================= ACCORDION CLICK ========================= */

  const toggleAccordion = (accordionId) => {
    setActiveAccordion((prev) => {
      if (prev === accordionId) return null;

      const acc = accordions.find((a) => a.id === accordionId);

      if (acc) {
        const first = acc.progress[0];

        setCurrentAccordion(acc.title);
        setCurrentComponent(first.component);
        setStepIndex(0);

        localStorage.setItem("CurrentComponent", first.label);
        localStorage.setItem("accordianIndex", 0);
      }

      return accordionId;
    });
  };

  /* ========================= NAV ========================= */

  async function handleNext(index, component, accordion) {
    await handleUpdateStatus();

    getCurrentComponent(component);

    setCurrentAccordion(accordion);
    setActiveAccordion(accordion);

    setStepIndex(index);

    fetchCompDetails();
  }

  function handleSkip(index, component, accordion) {
    getCurrentComponent(component);

    setCurrentAccordion(accordion);
    setActiveAccordion(accordion);

    setStepIndex(index);

    fetchCompDetails();
  }

  function handleBack(index, component, accordion) {
    getCurrentComponent(component);

    setCurrentAccordion(accordion);
    setActiveAccordion(accordion);

    setStepIndex(index);

    fetchCompDetails();
  }

  /* ========================= RESTORE ========================= */

  useEffect(() => {
    let savedComponent =
      localStorage.getItem("CurrentComponent") || "About Business";

    // Default fallback
    if (!savedComponent || savedComponent === "null") {
      savedComponent = "About Business";
      localStorage.setItem("CurrentComponent", savedComponent);
    }

    const found = findProgressByKey(savedComponent);

    // If saved value is invalid → fallback
    if (!found) {
      const first = accordions[0].progress[0];

      setActiveAccordion("About");
      setCurrentAccordion("About");
      setCurrentComponent(first.component);
      setStepIndex(0);

      localStorage.setItem("CurrentComponent", first.label);
      localStorage.setItem("accordianIndex", 0);

      return;
    }

    // Restore everything
    setActiveAccordion(found.accordionId);
    setCurrentAccordion(found.accordionTitle);
    setCurrentComponent(found.component);
    setStepIndex(found.index);

    localStorage.setItem("accordianIndex", found.index);
  }, [accordions]);


  /* ========================= LOADER ========================= */

  if (isLoading) {
    return (
      <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
        <LoadingIcon />
      </div>
    );
  }

  /* ========================= UI ========================= */

  return (
    <>
      <div className="h-screen overflow-hidden bg-white max-w-[1920px] mx-auto">
        <div className="flex flex-col sm:flex-row h-screen overflow-hidden">

          {/* SIDEBAR */}
          <div className="sm:min-h-screen flex flex-col items-center sm:w-64 w-full sm:min-w-[25%] px-4 sm:pl-14">

            <Link to="/" className="py-8 px-4">
              <LogoSVG />
            </Link>

            {isSessionExpired && (
              <Modal modalHandle={sessionExpiredPopup}>
                <SessionExpired handleNavigate={sessionExpiredPopup} />
              </Modal>
            )}

            <div className="w-full py-4 px-4 overflow-auto mt-5 sm:mt-20">

              {accordions.map((accordion) => (
                <Accordion
                  key={accordion.id}
                  title={accordion.title}
                  links={accordion.progress}
                  icon={accordion.icon}
                  getCurrentComponent={getCurrentComponent}
                  stepIndex={stepIndex}
                  currentAccordion={currentAccordion}
                  isActive={activeAccordion === accordion.id}
                  onClick={() => toggleAccordion(accordion.id)}
                />
              ))}

            </div>
          </div>

          {/* CONTENT */}
          <div className="w-full py-4 px-4 overflow-auto">

            {
              accordions.find((a) => a.id === activeAccordion)
                ?.upperComponent
            }

            {currentComponent}

          </div>
        </div>
      </div>
    </>
  );
};

export default CompanyEditProfile;
